/**
 * ServiceAdvisorList Component
 *
 * Sortable list of service advisors with expandable rows.
 * Includes column headers with sort indicators.
 */

import React, { useState, useCallback } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  BarsArrowDownIcon,
} from '@heroicons/react/24/outline';
import { ServiceAdvisor, SortConfig, SortField } from './types';
import { ServiceAdvisorRow } from './ServiceAdvisorRow';

interface ServiceAdvisorListProps {
  advisors: ServiceAdvisor[];
  sortConfig: SortConfig;
  onSortChange: (config: SortConfig) => void;
}

/**
 * Sort column configuration
 */
const SORT_COLUMNS: Array<{
  field: SortField;
  label: string;
  shortLabel: string;
  align: 'left' | 'right';
}> = [
  { field: 'name', label: 'Advisor', shortLabel: 'Name', align: 'left' },
  { field: 'laborGrossTotal', label: 'Labor Gross', shortLabel: 'Gross', align: 'right' },
  { field: 'roCountTotal', label: 'Closed ROs', shortLabel: 'Closed', align: 'right' },
  { field: 'openROCount', label: 'Open ROs', shortLabel: 'Open', align: 'right' },
  { field: 'elrTotal', label: 'ELR', shortLabel: 'ELR', align: 'right' },
  { field: 'avgHoursPerRoTotal', label: 'Hrs/RO', shortLabel: 'Hrs/RO', align: 'right' },
];

export const ServiceAdvisorList: React.FC<ServiceAdvisorListProps> = ({
  advisors,
  sortConfig,
  onSortChange,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Toggle expanded state for an advisor
  const toggleExpanded = useCallback((advisorId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(advisorId)) {
        next.delete(advisorId);
      } else {
        next.add(advisorId);
      }
      return next;
    });
  }, []);

  // Handle sort column click
  const handleSort = useCallback(
    (field: SortField) => {
      onSortChange({
        field,
        direction:
          sortConfig.field === field && sortConfig.direction === 'desc'
            ? 'asc'
            : 'desc',
      });
    },
    [sortConfig, onSortChange]
  );

  // Render sort indicator
  const renderSortIndicator = (field: SortField) => {
    if (sortConfig.field !== field) {
      return <BarsArrowDownIcon className="h-4 w-4 text-gray-300" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon className="h-4 w-4 text-orange-600" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 text-orange-600" />
    );
  };

  if (advisors.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No service advisor data available for this period.
      </div>
    );
  }

  return (
    <div>
      {/* Sort header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-100 rounded-t-lg border-b border-gray-200">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span className="font-medium">Sort by:</span>
          {SORT_COLUMNS.map((col) => (
            <button
              key={col.field}
              onClick={() => handleSort(col.field)}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                sortConfig.field === col.field
                  ? 'bg-orange-100 text-orange-700'
                  : 'hover:bg-gray-200 text-gray-600'
              }`}
            >
              <span>{col.shortLabel}</span>
              {renderSortIndicator(col.field)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">
            <span className="text-gray-700">Closed</span>
            <span className="mx-0.5">/</span>
            <span className="text-blue-600">Open</span>
            <span className="ml-1">ROs</span>
          </span>
          <span className="text-xs text-gray-500">
            {advisors.length} advisor{advisors.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Advisor rows */}
      <div className="bg-white rounded-b-lg">
        {advisors.map((advisor, index) => (
          <ServiceAdvisorRow
            key={advisor.advisorId}
            advisor={advisor}
            isExpanded={expandedIds.has(advisor.advisorId)}
            onToggle={() => toggleExpanded(advisor.advisorId)}
            rank={index + 1}
          />
        ))}
      </div>
    </div>
  );
};
